import React from 'react';
import { UserPlus, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from './HeroSection.module.scss';
import { useNavigate } from 'react-router-dom';
import { fireAnalytics } from '../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts';

interface HeroSectionProps {
  logo: string;
  isScrolled: boolean;
  scrollY: number;
  onScrollToSection: (section: "form" | "offers") => void;
  activeSection: "form" | "offers";
  totalOffers?: number;
  isLoadingOffers?: boolean;
  isMobile?: boolean;
  isCompact?: boolean;
  isFullHero?: boolean;
  isFullHeroViewport?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  logo,
  isScrolled,
  scrollY,
  onScrollToSection,
  activeSection,
  totalOffers = 0,
  isLoadingOffers = false,
  isMobile = false,
  isCompact = false,
  isFullHero = false,
  isFullHeroViewport = false
}) => {
  const navigate = useNavigate();
  const showCompactNav = isFullHeroViewport && isCompact;

  const heroHeight = isFullHeroViewport
    ? (isCompact ? 'auto' : '100dvh')
    : (isScrolled ? '20vh' : '40vh');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  return (
    <section
      className={`${styles.heroSection} ${showCompactNav ? styles.heroSection_compact : ''}`}
      style={!isFullHeroViewport ? { '--hero-height': heroHeight } as React.CSSProperties : {}}
    >
      <motion.div
        className={styles.heroOverlay}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className={styles.logoContainer} variants={itemVariants}>
          <img
            src={logo || "/placeholder.svg"}
            alt="Logo de Edify"
            className={styles.logo}
            onClick={() => navigate('/')}
          />
        </motion.div>

        {!showCompactNav && (
          <>
            <motion.div className={styles.heroContent} variants={itemVariants}>
              <h1 className={styles.heroTitle}>Trabajos reales en construcción</h1>
              <p className={styles.heroSubtitle}>
                Inscribite y accedé a ofertas de empresas que buscan personal.
              </p>
              <div className={styles.heroBenefits}>
                <span className={styles.heroBenefitChip}>100% Gratis</span>
                <span className={styles.heroBenefitChip}>Alertas por email</span>
                <span className={styles.heroBenefitChip}>Ofertas reales</span>
              </div>
            </motion.div>

            {isFullHero ? (
              <div className={styles.heroActions}>
                <button
                  className={`${styles.heroCta} ${styles.heroCta_mobileEnter}`}
                  onClick={() => onScrollToSection("offers")}
                >
                  <Briefcase size={20} />
                  Ver ofertas
                  {(isLoadingOffers || totalOffers > 0) && (
                    <span className={styles.heroCtaBadge}>
                      {isLoadingOffers ? (
                        <span className={styles.heroCtaBadgeSpinner} />
                      ) : (
                        totalOffers
                      )}
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <motion.div
                className={styles.heroActions}
                variants={containerVariants}
              >
                <motion.button
                  className={styles.heroCta}
                  onClick={() => {
                    fireAnalytics("work_bag_hero_desktop_inscribirme_click", {
                      session_id: getSessionId(),
                      device_category: getDeviceCategory(),
                      category: "work_bag",
                      action: "hero_cta_click",
                      source: "hero_desktop",
                      cta_label: "inscribirme",
                    });
                    onScrollToSection("form");
                  }}
                  variants={buttonVariants}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <UserPlus size={20} />
                  Inscribirme
                </motion.button>
              </motion.div>
            )}
          </>
        )}

        {showCompactNav && (
          <motion.div className={styles.heroCompactNav} variants={itemVariants}>
            <button
              className={`${styles.heroCompactButton} ${activeSection === "form" ? styles.active : ""}`}
              onClick={() => onScrollToSection("form")}
            >
              <UserPlus size={20} />
              <span className={styles.heroCompactButtonText}>Inscribirme</span>
            </button>
            <button
              className={`${styles.heroCompactButton} ${activeSection === "offers" ? styles.active : ""}`}
              onClick={() => onScrollToSection("offers")}
            >
              <Briefcase size={20} />
              <span className={styles.heroCompactButtonText}>Ofertas</span>
              {(isLoadingOffers || totalOffers > 0) && (
                <span className={styles.heroCompactBadge}>
                  {isLoadingOffers ? (
                    <span className={styles.heroCompactBadgeSpinner} />
                  ) : (
                    totalOffers
                  )}
                </span>
              )}
            </button>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default HeroSection;
